import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { ZoomMtg } from '@zoom/meetingsdk';
import type { ZoomConfigType } from './zoomTypes';
import type { createZoomHooks } from './zoomHooks';
import { Box } from '@mui/material';
import { PORTAL_TYPES } from '../constants/portalType';
import './ZoomCall.css';

type ZoomHooks = ReturnType<typeof createZoomHooks>;

interface ZoomCallProps {
  zoomSdkKey: string;
  zoomHooks: ZoomHooks;
  chatPanel?: ReactNode;
  portalType?: string;
  userId: number;
  onChatToggle?: (isOpen: boolean) => void;
}
type ZoomCurrentUserResponse = {
  result: {
    currentUser: {
      role: number;
      displayName?: string;
      userId?: number;
    };
  };
};

const ZoomCall: FunctionComponent<ZoomCallProps> = ({
  zoomSdkKey,
  zoomHooks,
  chatPanel,
  userId,
  portalType = PORTAL_TYPES.CLINICAL,
  onChatToggle,
}) => {
  const { role, username, meetingId, meetingPassword, zoomKey, caseId } =
    useParams();
  const { mutate: callGetZoomSignature } =
    zoomHooks.useZoomSignature(portalType);
  const [isMeetingJoined, setIsMeetingJoined] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [callDuration, setCallDuration] = useState(0);
  const chatBtnRef = useRef<HTMLButtonElement | null>(null);
  const zoomContainerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<HTMLDivElement | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toggleChatRef = useRef<() => void>(() => {});
  const isChatOpenRef = useRef(isChatOpen);
  const onChatToggleRef = useRef(onChatToggle);

  const { UnreadCount } = zoomHooks.useGetCompleteUnReadNotificationCount(
    userId,
    Number(caseId),
    portalType
  );

  useEffect(() => {
    localStorage.setItem('refresh-consult-tabs', Date.now().toString());
  }, []);

  useEffect(() => {
    const mountedAt = Date.now();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `closeZoom-${caseId}` && e.newValue) {
        localStorage.removeItem(`closeZoom-${caseId}`);
        window.close();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const checkCloseSignal = setInterval(() => {
      const closeSignal = localStorage.getItem(`closeZoom-${caseId}`);
      // Ignore the signal written by the call start that opened this tab
      if (closeSignal && Number(closeSignal) > mountedAt) {
        localStorage.removeItem(`closeZoom-${caseId}`);
        window.close();
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkCloseSignal);
    };
  }, [caseId]);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => {
      const next = !prev;

      // If opening chat and participants panel is open, close participants
      if (next && isParticipantsOpen) {
        const zmmtgRoot = document.getElementById('zmmtg-root');
        if (zmmtgRoot) {
          // Try multiple selectors to find and click the participants button
          const selectors = [
            'button[aria-label*="Participants"]',
            'button[aria-label="Participants"]',
            'button[title*="Participants"]',
            '[class*="participants-button"]',
            '[class*="participant-button"]',
          ];

          let clicked = false;
          for (const selector of selectors) {
            const button = zmmtgRoot.querySelector(
              selector
            ) as HTMLButtonElement;
            if (button && button.offsetParent !== null) {
              button.click();
              clicked = true;
              break;
            }
          }

          // If button click didn't work, try finding close button in participants panel
          if (!clicked) {
            const closeButton =
              (zmmtgRoot.querySelector(
                '[class*="participants-"] button[aria-label*="Close"]'
              ) as HTMLButtonElement) ||
              (zmmtgRoot.querySelector(
                '[class*="participants-"] button[aria-label*="close"]'
              ) as HTMLButtonElement) ||
              (zmmtgRoot.querySelector(
                '[role="dialog"] button[aria-label*="Close"]'
              ) as HTMLButtonElement);
            if (closeButton) {
              closeButton.click();
            }
          }
        }
      }

      onChatToggle?.(next);
      return next;
    });
  }, [onChatToggle, isParticipantsOpen]);

  useEffect(() => {
    toggleChatRef.current = toggleChat;
  }, [toggleChat]);

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  useEffect(() => {
    onChatToggleRef.current = onChatToggle;
  }, [onChatToggle]);

  useEffect(() => {
    const badge = document.getElementById('qler-chat-badge');

    if (!badge) return;

    const count = UnreadCount?.unreadMessageCount ?? 0;

    badge.style.display = count > 0 ? 'flex' : 'none';
    badge.textContent = count > 99 ? '99+' : String(count);
  }, [UnreadCount]);

  // Timer effect - starts when meeting is joined
  useEffect(() => {
    if (!isMeetingJoined) return;

    const storageKey = `zoom-call-start-${meetingId}`;
    let startTime = Date.now();

    // Check if there's a stored start time from before refresh
    const storedStartTime = sessionStorage.getItem(storageKey);
    if (storedStartTime) {
      startTime = Number.parseInt(storedStartTime, 10);
    } else {
      // First time joining, store the start time
      sessionStorage.setItem(storageKey, startTime.toString());
    }

    timerIntervalRef.current = globalThis.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setCallDuration(elapsed);
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isMeetingJoined, meetingId]);

  // Format timer as HH:MM:SS
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Hide unwanted Zoom toolbar buttons after meeting joins
  useEffect(() => {
    if (!isMeetingJoined) return;

    const userRoleNum = Number.parseInt(role ?? '0');
    const isHost = userRoleNum === 1;

    // Labels to hide: for participant hide all extra, for host keep only Participants
    const labelsToHide = isHost
      ? [
          'raise hand',
          'security',
          'more',
          'reactions',
          'apps',
          'captions',
          'caption',
          'show captions',
          'whiteboards',
          'whiteboard',
          'settings',
        ]
      : [
          'participants',
          'raise hand',
          'security',
          'more',
          'reactions',
          'apps',
          'captions',
          'caption',
          'show captions',
          'whiteboards',
          'whiteboard',
          'settings',
        ];

    const processedElements = new WeakSet<Element>();

    const hideToolbarButtons = () => {
      const zmmtgRoot = document.getElementById('zmmtg-root');
      if (!zmmtgRoot) return;

      // Ensure Zoom UI is visible (ready class handles the fade-in via CSS)
      if (!zmmtgRoot.classList.contains('ready')) {
        zmmtgRoot.classList.add('ready');
      }

      // Scope search to footer area only to avoid hiding header elements (e.g. View button)
      const footerArea =
        zmmtgRoot.querySelector('#wc-footer') ||
        zmmtgRoot.querySelector('[class*="footer"]');
      if (!footerArea) return;

      // Find all span elements that contain button label text within footer
      const allSpans = footerArea.querySelectorAll('span');
      allSpans.forEach((span) => {
        if (processedElements.has(span)) return;

        const text = span.textContent?.trim().toLowerCase() || '';
        if (!labelsToHide.includes(text)) return;

        processedElements.add(span);

        // Walk up the DOM to find the clickable button wrapper (stay within footer)
        let el: HTMLElement | null = span as HTMLElement;
        for (let i = 0; i < 6 && el; i++) {
          el = el.parentElement;
          if (!el || el === footerArea) break;
          const tag = el.tagName.toLowerCase();
          const elRole = el.getAttribute('role');
          if (
            tag === 'button' ||
            elRole === 'button' ||
            elRole === 'menuitem'
          ) {
            el.style.display = 'none';
            processedElements.add(el);
            return;
          }
        }

        // Fallback: walk up to find the right container div to hide (stay within footer)
        let current: HTMLElement | null = span as HTMLElement;
        for (let i = 0; i < 4 && current; i++) {
          const parentEl: HTMLElement | null = current.parentElement;
          if (!parentEl || parentEl === footerArea) break;
          if (parentEl.children.length === 1) {
            current = parentEl;
          } else {
            current.style.display = 'none';
            processedElements.add(current);
            return;
          }
        }
        if (current && current !== footerArea) {
          current.style.display = 'none';
          processedElements.add(current);
        }
      });
    };

    // Run after short delays and continuously via observer
    const timeouts = [50, 150, 300, 600, 1000, 2000, 3000, 5000].map((ms) =>
      setTimeout(hideToolbarButtons, ms)
    );

    const observer = new MutationObserver(() => {
      requestAnimationFrame(hideToolbarButtons);
    });

    const zmmtgRoot = document.getElementById('zmmtg-root');
    if (zmmtgRoot) {
      observer.observe(zmmtgRoot, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      timeouts.forEach(clearTimeout);
      observer.disconnect();
    };
  }, [role, isMeetingJoined]);

  // Inject a custom Chat button into the DOM after Zoom meeting joins
  useEffect(() => {
    if (!isMeetingJoined || !chatPanel) return;

    // Find and wrap the Zoom meeting container
    const zoomMeetingContainer = document.querySelector(
      '#zmmtg-root'
    ) as HTMLDivElement;
    if (zoomMeetingContainer) {
      zoomMeetingContainer.style.transition = 'width 0.3s ease-in-out';
      zoomContainerRef.current = zoomMeetingContainer;
    }

    // Create portal container for ChatQLER split screen
    const container = document.createElement('div');
    container.id = 'qler-chat-portal';
    container.style.cssText = `
      position: fixed;
      top: 0;
      right: 0;
      width: 380px;
      height: 100vh;
      z-index: 99999;
      pointer-events: none;
      transform: translateX(100%);
      transition: transform 0.3s ease-in-out;
    `;
    document.body.appendChild(container);
    setPortalContainer(container);

    // Create the floating Chat button
    const chatBtn = document.createElement('button');
    chatBtn.id = 'qler-zoom-chat-btn';
    chatBtn.title = 'Open Chat';
    chatBtn.style.cssText = `
      position: fixed;
      bottom: 7px;
      right: 100px;
      z-index: 99999;
      width: 38px;
      height: 38px;
      border-radius: 50%;
      border: none;
      background-color: #0E72ED;
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.3);
      transition: background-color 0.3s, right 0.3s ease-in-out;
    `;
    chatBtn.innerHTML = `
  <div style="position: relative; display: flex; align-items: center; justify-content: center;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 2H4C2.9 2 2.01 2.9 2.01 4L2 22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM6 9H18V11H6V9ZM14 14H6V12H14V14ZM18 8H6V6H18V8Z" fill="currentColor"/>
    </svg>

    <span
      id="qler-chat-badge"
      style="
        position: absolute;
        top: -14px;
        right: -18px;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: 999px;
        background: red;
        color: white;
        font-size: 11px;
        font-weight: 600;
        display: none;
        align-items: center;
        justify-content: center;
        line-height: 18px;
        box-sizing: border-box;
      "
    >
      0
    </span>
  </div>
`;
    chatBtn.onmouseenter = () => {
      chatBtn.style.backgroundColor = '#0B5AC7';
    };
    chatBtn.onmouseleave = () => {
      chatBtn.style.backgroundColor = '#0E72ED';
    };
    chatBtn.onclick = () => {
      toggleChatRef.current();
    };
    document.body.appendChild(chatBtn);
    chatBtnRef.current = chatBtn;

    // Create timer display in top left
    const timerDiv = document.createElement('div');
    timerDiv.id = 'qler-zoom-timer';
    timerDiv.style.cssText = `
      position: fixed;
      top: 8px;
      left: 100px;
      z-index: 99999;
      background-color: rgba(0, 0, 0, 0.6);
      color: white;
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 18px;
      font-weight: 500;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      pointer-events: none;
      box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
    `;
    timerDiv.textContent = '00:00:00';
    document.body.appendChild(timerDiv);
    timerRef.current = timerDiv;

    return () => {
      chatBtn.remove();
      container.remove();
      timerDiv.remove();
      setPortalContainer(null);
      chatBtnRef.current = null;
      timerRef.current = null;
      if (zoomContainerRef.current) {
        zoomContainerRef.current.style.width = '';
      }
      zoomContainerRef.current = null;
    };
  }, [isMeetingJoined]);

  // Update timer display
  useEffect(() => {
    if (timerRef.current) {
      timerRef.current.textContent = formatTime(callDuration);
    }
  }, [callDuration]);

  // Detect Zoom participants panel and close chat if it opens
  useEffect(() => {
    if (!isMeetingJoined) return;

    const checkParticipantsPanel = () => {
      const zmmtgRoot = document.getElementById('zmmtg-root');
      if (!zmmtgRoot) return;

      // Check for participants panel (Zoom uses various selectors)
      const participantsPanel =
        zmmtgRoot.querySelector('[class*="participants-"]') ||
        zmmtgRoot.querySelector(
          '[aria-label*="Participants"][role="dialog"]'
        ) ||
        zmmtgRoot.querySelector('[class*="participant-container"]');

      const isOpen =
        participantsPanel !== null &&
        (participantsPanel as HTMLElement).offsetParent !== null;

      setIsParticipantsOpen((prev) => {
        if (prev === isOpen) return prev;
        return isOpen;
      });

      // Close chat if participants panel opens
      if (isOpen && isChatOpenRef.current) {
        setIsChatOpen(false);
        onChatToggleRef.current?.(false);
      }
    };

    // Check immediately and set up observer
    checkParticipantsPanel();

    const observer = new MutationObserver(() => {
      checkParticipantsPanel();
    });

    const zmmtgRoot = document.getElementById('zmmtg-root');
    if (zmmtgRoot) {
      observer.observe(zmmtgRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [isMeetingJoined]);

  // Update layout when chat panel opens/closes (split screen)
  useEffect(() => {
    if (portalContainer) {
      portalContainer.style.transform = isChatOpen
        ? 'translateX(0)'
        : 'translateX(100%)';
    }
    if (zoomContainerRef.current) {
      zoomContainerRef.current.style.width = isChatOpen
        ? 'calc(100% - 380px)'
        : '100%';
    }
    if (chatBtnRef.current) {
      // Adjust button position based on chat and participants panel state
      if (isChatOpen) {
        chatBtnRef.current.style.right = '480px';
      } else if (isParticipantsOpen) {
        chatBtnRef.current.style.right = '500px';
      } else {
        chatBtnRef.current.style.right = '100px';
      }
    }
  }, [isChatOpen, isParticipantsOpen, portalContainer]);

  // Detect iOS device for CSS-based fallback styles and force footer visible
  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
    if (isIOS) {
      document.body.classList.add('ios-device');
    }

    if (!isIOS || !isMeetingJoined) {
      return () => {
        document.body.classList.remove('ios-device');
      };
    }

    // Force footer visible on iPad - Zoom SDK hides it via inline styles on touch devices
    // Only override opacity and visibility to avoid breaking the SDK's own layout
    const forceFooterVisible = () => {
      const zmmtgRoot = document.getElementById('zmmtg-root');
      if (!zmmtgRoot) return;

      const footer = zmmtgRoot.querySelector('#wc-footer') as HTMLElement;
      if (!footer) return;

      if (
        footer.style.opacity === '0' ||
        footer.style.visibility === 'hidden'
      ) {
        footer.style.opacity = '1';
        footer.style.visibility = 'visible';
      }
    };

    forceFooterVisible();

    const observer = new MutationObserver(() => {
      forceFooterVisible();
    });

    const zmmtgRoot = document.getElementById('zmmtg-root');
    if (zmmtgRoot) {
      observer.observe(zmmtgRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    return () => {
      observer.disconnect();
      document.body.classList.remove('ios-device');
    };
  }, [isMeetingJoined]);

  // Apply role-specific CSS class to body for host/participant styling
  useEffect(() => {
    const userRoleNum = Number.parseInt(role ?? '0');
    const isHost = userRoleNum === 1;

    if (isHost) {
      document.body.classList.add('zoom-host-view');
    } else {
      document.body.classList.add('zoom-participant-view');
    }

    return () => {
      document.body.classList.remove('zoom-host-view');
      document.body.classList.remove('zoom-participant-view');
    };
  }, [role]);

  const joinZoomCall = (zoomConfig: ZoomConfigType) => {
    ZoomMtg.join({
      ...zoomConfig,
      success: async () => {
        ZoomMtg.getCurrentUser({
          success: (res: ZoomCurrentUserResponse) => {
            const zoomRole = res.result.currentUser.role;
            const urlRoleNum = Number.parseInt(role ?? '0');
            const isClinicalUser = urlRoleNum === 1;

            if (zoomRole === 1) {
              sessionStorage.setItem('errorFlag', 'false');
            } else {
              sessionStorage.setItem('errorFlag', 'true');
            }

            // If this is a clinical user but not currently host, reclaim host
            if (isClinicalUser && zoomRole !== 1) {
              console.log('Clinical user reclaiming host...');
              ZoomMtg.reclaimHost({
                success: () => {
                  console.log('Successfully reclaimed host');
                  sessionStorage.setItem('errorFlag', 'false');
                },
                error: (error: Error) => {
                  console.log('Failed to reclaim host:', error);
                },
              });
            }
          },
        });

        // Set session storage and move Zoom container if needed
        sessionStorage.setItem('zoomMeeting', 'true');

        const zoomRoot = document.getElementById('zmmtg-root');
        if (zoomRoot) {
          zoomRoot.style.display = 'block';
          const mainContainer = document.getElementById('zoomAllContainer');
          if (mainContainer) {
            mainContainer.appendChild(zoomRoot);
          }
          // Reveal Zoom UI after role CSS has preemptively hidden unwanted buttons
          zoomRoot.classList.add('ready');
        }

        setIsMeetingJoined(true);
      },

      error: (error: Error) => {
        console.log('zoom join error', error);
      },
    });
  };

  const startZoomCall = async () => {
    if (ZoomMtg) {
      ZoomMtg.preLoadWasm();
      ZoomMtg.prepareWebSDK();
    } else {
      console.error('ZoomMtg is not loaded properly.');
    }

    const requestData = {
      meetingId: meetingId ?? '0',
      role: Number.parseInt(role ?? '0'),
      zoomKey: atob(zoomKey ?? ''),
    };
    callGetZoomSignature(requestData, {
      onSuccess: (response) => {
        const isHost = Number.parseInt(role ?? '0') === 1;
        ZoomMtg.init({
          leaveUrl: `${globalThis.location.origin}/zoom-call-end/${meetingId}/${caseId}`,
          isSupportNonverbal: false,
          isSupportCC: false,
          isSupportPolling: false,
          isSupportQA: false,
          isSupportBreakout: false,
          screenShare: isHost,
          isSupportChat: isHost,
          showMeetingHeader: true,
          disableReport: true,
          disableInvite: false,
          meetingInfo: [
            'topic',
            'host',
            'mn',
            'pwd',
            'telPwd',
            'invite',
            'participant',
            'dc',
            'enctype',
          ],
          success: function () {
            const zoomConfig: ZoomConfigType = {
              sdkKey: zoomSdkKey,
              signature: response.signature,
              meetingNumber: meetingId ?? '',
              passWord: meetingPassword ?? '',
              userName: username ?? '',
              zak: undefined as string | undefined,
            };

            if (response?.token) {
              zoomConfig.zak = response?.token;
            }

            joinZoomCall(zoomConfig);
          },
          error: (error: Error) => {
            console.log('zoom init error', error);
          },
        });
      },
      onError: (error: Error) => {
        console.log('callGetZoomSignature error', error);
      },
    });
  };

  useEffect(() => {
    startZoomCall();
  }, []);

  // Cleanup timer storage when component unmounts (user leaves the call)
  useEffect(() => {
    return () => {
      const storageKey = `zoom-call-start-${meetingId}`;
      sessionStorage.removeItem(storageKey);
    };
  }, [meetingId]);

  return (
    <>
      {portalContainer &&
        createPortal(
          <Box
            className={`zoom-chat-portal ${isChatOpen ? 'zoom-chat-portal-visible' : 'zoom-chat-portal-hidden'}`}
          >
            {chatPanel}
          </Box>,
          portalContainer
        )}
    </>
  );
};

export default ZoomCall;
